"use client";

import React from "react";
import { BarChart3, Users, TrendingUp, Globe, } from "lucide-react";
import { motion } from "motion/react";
import { TIndustriesConfig } from "@/app/config";

const platformFeaturesIcons = [
  {
    icon: BarChart3,
  },
  {
    icon: Users,
  },
  {
    icon: TrendingUp,
  },
  {
    icon: Globe,
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      duration: 0.6
    }
  }
};



export default function EntertainmentIntelligencePlatform({config}:{config: TIndustriesConfig['EntertainmentIntelligencePlatform']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-gray-50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Feature Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={containerVariants}
        >
          {config.platformFeatures.map((feature, index) =>{
            const Icons= platformFeaturesIcons[index].icon;
            return (
            <motion.div
              key={index}
              className={`${feature.bgColor} ${feature.borderColor} border rounded-xl p-8 cursor-pointer group`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.9
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{
                y: -10,
                scale: 1.03,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ scale: 0.98 }}
              style={{ 
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
              }}
            >
              {/* Icon */}
              <motion.div 
                className="mb-6"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <motion.div 
                  className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300"
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      scale: 0.5,
                      rotate: -45
                    },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      rotate: 0,
                      transition: {
                        duration: 0.6,
                        ease: "backOut"
                      }
                    }
                  }}
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <motion.div
                    variants={ {
                      hidden: { opacity: 0, scale: 0.5 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0.4,
                          ease: "easeOut"
                        }
                      }
                    }}
                    whileHover={{
                      scale: 1.2,
                      rotate: 10,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                  >
                    <Icons className={`w-6 h-6 ${feature.iconColor} transition-colors duration-300`} />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Content */}
              <motion.div 
                className="mb-6"
                variants={ {
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.2 }}
              >
                <motion.h3 
                  className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-blue-700 transition-colors duration-300"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                >
                  {feature.title}
                </motion.h3>
                <motion.p 
                  className="text-gray-600 text-base leading-relaxed"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.4, duration: 0.6 }}
                >
                  {feature.description}
                </motion.p>
              </motion.div>

              {/* Learn More Link */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut"
                    }
                  }
                }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.5 }}
              >
                <motion.button 
                  className={`${feature.linkColor} text-sm font-semibold hover:underline flex items-center gap-1 group/link`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial="rest"
                  
                >
                  <span>{feature.linkText}</span>
                  <motion.span 
                    className="text-xs"
                    variants={ {
                      rest: { x: 0 },
                      hover: { 
                        x: 5,
                        transition: {
                          duration: 0.3,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    →
                  </motion.span>
                </motion.button>
              </motion.div>
            </motion.div>
          )})}
        </motion.div>
      </div>
    </motion.section>
  );
}